import type {FetchIdentificationInfoResponse} from '@rocket-ui/lk-api';
import {
	combinesDocumentPhotosWithDetails,
	getRegistrationDocumentsFlowKey,
	resolveDocumentsFlow,
} from './documentsFlow';
import {
	applyApiPhotosToDocumentsUpload,
	createEmptyDocumentsUploadValues,
	extractIdentificationDocumentPhotos,
	isDocumentPhotosStepValid,
	isResidenceDocumentStepValid,
	type DocumentsUploadValues,
	type IdentificationDocumentPhotos,
} from './documentsUpload';
import type {ForeignDocumentDetailsStepValues} from './foreignDocument';
import type {PassportDetailsStepValues} from './passport';
import {
	EMPTY_FOREIGN_DOCUMENT_DETAILS,
	isKazakhstanCitizenship,
	mapIdentificationDocumentToForeignDetails,
	resolveKazakhIdentificationDocumentType,
	usesForeignDocumentDetailsForm,
} from './foreignDocument';
import {extractInnDigits, isInnDigitsValid} from './inn';
import {EMPTY_PASSPORT_DETAILS, mapIdentificationDocumentToPassportDetails} from './passport';
import {mapIdentificationInfoToPersonalInfo} from './personalInfo';
import {resolveRegistrationFlow} from './registrationFlow';
import {RegistrationStep} from './steps';
import {isTaxIdentifierStepValid} from './taxIdentifier';
import type {PersonalInfoStepValues} from './types';
import {isDocumentDetailsStepValid, isPersonalInfoStepValid} from './validation';

const readInfoString = (
	data: FetchIdentificationInfoResponse | undefined,
	key: 'inn' | 'placeOfResidence',
): string => {
	const info = data?.info;

	if (!info || typeof info !== 'object' || !(key in info)) {
		return '';
	}

	const value = (info as Record<string, unknown>)[key];

	return typeof value === 'string' ? value.trim() : '';
};

const toPersonalInfo = (data: FetchIdentificationInfoResponse | undefined): PersonalInfoStepValues => {
	const prefill = mapIdentificationInfoToPersonalInfo(data);

	return {
		citizenshipOksmCode: prefill.citizenshipOksmCode ?? null,
		citizenshipCountryName: null,
		residenceOksmCode: prefill.residenceOksmCode ?? null,
		residenceCountryName: null,
		gender: prefill.gender ?? null,
		secondName: prefill.secondName ?? '',
		firstName: prefill.firstName ?? '',
		patronymic: prefill.patronymic ?? '',
		email: prefill.email ?? '',
	};
};

/**
 * Куда открыть `/registration` после перезагрузки.
 * Смотрим сохранённые данные и берём первый шаг, который ещё не заполнен:
 * анкета → документы → налоговый идентификатор → место проживания → сверка.
 */
export const resolveRegistrationResumeStep = (
	data: FetchIdentificationInfoResponse | undefined,
): RegistrationStep => {
	const personalInfo = toPersonalInfo(data);

	if (!isPersonalInfoStepValid(personalInfo)) {
		return RegistrationStep.PersonalInfo;
	}

	const foreignDetails = {
		...EMPTY_FOREIGN_DOCUMENT_DETAILS,
		...mapIdentificationDocumentToForeignDetails(data?.identificationDocument),
	};
	const passportDetails = {
		...EMPTY_PASSPORT_DETAILS,
		...mapIdentificationDocumentToPassportDetails(data?.identificationDocument),
	};
	const usesForeignDocumentDetails = usesForeignDocumentDetailsForm(
		personalInfo.citizenshipOksmCode,
		personalInfo.residenceOksmCode,
	);
	const documentsFlow = resolveDocumentsFlow({
		citizenshipOksmCode: personalInfo.citizenshipOksmCode,
		residenceOksmCode: personalInfo.residenceOksmCode,
		identificationDocumentType: isKazakhstanCitizenship(personalInfo.citizenshipOksmCode)
			? resolveKazakhIdentificationDocumentType(foreignDetails.documentType)
			: null,
	});
	const residenceAddress = readInfoString(data, 'placeOfResidence');
	const documentsUpload = applyApiPhotosToDocumentsUpload(
		documentsFlow,
		{
			...createEmptyDocumentsUploadValues(documentsFlow),
			...(residenceAddress ? {residenceAddress} : {}),
		},
		extractIdentificationDocumentPhotos(data),
	);
	const photosOnDetails = combinesDocumentPhotosWithDetails(personalInfo.citizenshipOksmCode);
	const detailsReady = isDocumentDetailsStepValid({
		useForeignDocumentDetails: usesForeignDocumentDetails,
		russianDetails: passportDetails,
		foreignDetails,
		citizenshipOksmCode: personalInfo.citizenshipOksmCode,
		residenceOksmCode: personalInfo.residenceOksmCode,
	});
	const photosReady = isDocumentPhotosStepValid(documentsFlow, documentsUpload);
	const residenceReady = isResidenceDocumentStepValid(documentsFlow, documentsUpload);

	if (!photosOnDetails && !photosReady) {
		return RegistrationStep.DocumentPhotos;
	}

	if (!detailsReady || (photosOnDetails && !photosReady)) {
		return RegistrationStep.DocumentDetails;
	}

	const registrationFlow = resolveRegistrationFlow({
		citizenshipOksmCode: personalInfo.citizenshipOksmCode,
		residenceOksmCode: personalInfo.residenceOksmCode,
		workStatus: null,
	});
	const rawInn = readInfoString(data, 'inn');
	const hasTaxId =
		registrationFlow.taxIdType === 'russian_inn'
			? isInnDigitsValid(extractInnDigits(rawInn))
			: isTaxIdentifierStepValid({taxIdentifier: rawInn});

	if (!hasTaxId) {
		return RegistrationStep.IdentificationInn;
	}

	if (!residenceReady) {
		return RegistrationStep.ResidenceDocument;
	}

	return RegistrationStep.IdentityReview;
};

export type RegistrationResumePrefill = {
	step: RegistrationStep;
	personalInfo: Partial<PersonalInfoStepValues>;
	passportDetails: Partial<PassportDetailsStepValues>;
	foreignDocumentDetails: Partial<ForeignDocumentDetailsStepValues>;
	inn: string;
	taxIdentifier: string;
	residenceAddress: string;
	photos: IdentificationDocumentPhotos | null;
	documentsUpload: DocumentsUploadValues;
	documentsFlowKey: string;
};

/** Шаг и значения полей, которые можно подставить из GET /identification/info. */
export const readRegistrationResume = (
	data: FetchIdentificationInfoResponse | undefined,
): RegistrationResumePrefill => {
	const personalInfo = mapIdentificationInfoToPersonalInfo(data);
	const foreignDocumentDetails = mapIdentificationDocumentToForeignDetails(data?.identificationDocument);
	const passportDetails = mapIdentificationDocumentToPassportDetails(data?.identificationDocument);
	const fullPersonalInfo = toPersonalInfo(data);
	const foreignDetails = {...EMPTY_FOREIGN_DOCUMENT_DETAILS, ...foreignDocumentDetails};
	const documentsFlow = resolveDocumentsFlow({
		citizenshipOksmCode: fullPersonalInfo.citizenshipOksmCode,
		residenceOksmCode: fullPersonalInfo.residenceOksmCode,
		identificationDocumentType: isKazakhstanCitizenship(fullPersonalInfo.citizenshipOksmCode)
			? resolveKazakhIdentificationDocumentType(foreignDetails.documentType)
			: null,
	});
	const residenceAddress = readInfoString(data, 'placeOfResidence');
	const rawInn = readInfoString(data, 'inn');

	return {
		step: resolveRegistrationResumeStep(data),
		personalInfo,
		passportDetails,
		foreignDocumentDetails,
		inn: extractInnDigits(rawInn),
		taxIdentifier: rawInn,
		residenceAddress,
		photos: extractIdentificationDocumentPhotos(data),
		documentsUpload: applyApiPhotosToDocumentsUpload(
			documentsFlow,
			{
				...createEmptyDocumentsUploadValues(documentsFlow),
				...(residenceAddress ? {residenceAddress} : {}),
			},
			extractIdentificationDocumentPhotos(data),
		),
		documentsFlowKey: getRegistrationDocumentsFlowKey(
			fullPersonalInfo.citizenshipOksmCode,
			fullPersonalInfo.residenceOksmCode,
			foreignDocumentDetails.documentType ?? null,
		),
	};
};
